import { Toast as RToast } from "radix-ui";
import { CircleAlert, CircleCheck, Info, X } from "lucide-react";
import { useSyncExternalStore } from "react";
import { useT } from "../i18n";
import { cn } from "../lib/cn";

type ToastTone = "success" | "error" | "info";

interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
}

let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function publish(next: ToastItem[]) {
  items = next;
  for (const listener of listeners) listener();
}

const add = (tone: ToastTone) => (title: string, description?: string) => publish([...items, { id: nextId++, tone, title, description }].slice(-3));

/** Callable from anywhere, including mutation handlers outside React. */
export const toast = { success: add("success"), error: add("error"), info: add("info") };

const ICONS = { success: CircleCheck, error: CircleAlert, info: Info };
const ICON_COLORS = { success: "text-success", error: "text-danger", info: "text-info" };

export function Toaster() {
  const t = useT();
  const current = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => items,
  );
  return (
    <RToast.Provider swipeDirection="down" label={t("toast.region")}>
      {current.map((item) => {
        const Icon = ICONS[item.tone];
        return (
          <RToast.Root
            key={item.id}
            type={item.tone === "error" ? "foreground" : "background"}
            duration={item.tone === "error" ? 8000 : 4000}
            onOpenChange={(open) => {
              if (!open) publish(items.filter((other) => other.id !== item.id));
            }}
            className="flex w-full items-start gap-3 rounded-md border border-border bg-surface-raised p-3.5 shadow-overlay data-[state=open]:animate-pop-in data-[state=closed]:animate-fade-out data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)]"
          >
            <Icon className={cn("mt-0.5 size-5 shrink-0", ICON_COLORS[item.tone])} aria-hidden />
            <div className="min-w-0 flex-1">
              <RToast.Title className="text-sm font-medium text-fg">{item.title}</RToast.Title>
              {item.description ? <RToast.Description className="mt-0.5 text-sm text-fg-muted">{item.description}</RToast.Description> : null}
            </div>
            <RToast.Close aria-label={t("common.close")} className="-m-2 inline-flex size-11 shrink-0 items-center justify-center rounded-sm text-fg-muted hover:text-fg md:size-8">
              <X className="size-4" aria-hidden />
            </RToast.Close>
          </RToast.Root>
        );
      })}
      <RToast.Viewport className="max-md:[body:has([role=alertdialog],[role=dialog])_&]:top-[var(--vp-top)] max-md:[body:has([role=alertdialog],[role=dialog])_&]:translate-y-0 fixed bottom-auto left-1/2 top-[calc(var(--vp-top)+var(--vp-height)-var(--bottom-bar))] z-50 flex w-full max-w-md -translate-x-1/2 -translate-y-full flex-col gap-2 p-4 outline-none md:left-auto md:right-0 md:top-[calc(var(--vp-top)+var(--vp-height))] md:translate-x-0" />
    </RToast.Provider>
  );
}
